import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download, FileSpreadsheet } from "lucide-react";
import { exportToCsv } from "@/lib/exportCsv";
import { RotationEligibilityAlerts } from "./RotationEligibilityAlerts";
import { RotationReportsView } from "./RotationReportsView";

interface Group {
  id: string;
  name: string;
  semester: string;
  class: string;
  field_sites: { name: string } | null;
  site_name_override: string | null;
}

interface Row {
  student_id: string;
  student_name: string;
  student_number: string;
  scheduled_days: number;
  present_days: number;
  rate: number;
}

export function TuitionReports() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupId, setGroupId] = useState<string>("");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("field_groups")
        .select("id, name, semester, class, site_name_override, field_sites(name)")
        .order("name");
      setGroups((data ?? []) as any);
    })();
  }, []);

  useEffect(() => {
    if (!groupId) { setRows([]); return; }
    setLoading(true);
    (async () => {
      const [{ data: members }, { data: dates }, { data: scans }] = await Promise.all([
        supabase
          .from("field_group_members")
          .select("student_id, students(id, first_name, last_name, student_number)")
          .eq("group_id", groupId),
        supabase
          .from("field_group_dates")
          .select("placement_date")
          .eq("group_id", groupId),
        supabase
          .from("attendance_records")
          .select("student_id, attendance_date")
          .eq("field_group_id", groupId)
          .eq("attendance_type", "tuition")
          .eq("status", "present"),
      ]);
      const scheduled = (dates ?? []).length;
      const presentMap = new Map<string, Set<string>>();
      ((scans ?? []) as any[]).forEach((s) => {
        if (!presentMap.has(s.student_id)) presentMap.set(s.student_id, new Set());
        presentMap.get(s.student_id)!.add(s.attendance_date);
      });
      const result: Row[] = ((members ?? []) as any[]).map((m) => {
        const present = presentMap.get(m.student_id)?.size ?? 0;
        const rate = scheduled > 0 ? Math.round((present / scheduled) * 100) : 0;
        return {
          student_id: m.student_id,
          student_name: m.students ? `${m.students.first_name} ${m.students.last_name}` : "—",
          student_number: m.students?.student_number ?? "—",
          scheduled_days: scheduled,
          present_days: present,
          rate,
        };
      }).sort((a, b) => a.student_name.localeCompare(b.student_name));
      setRows(result);
      setLoading(false);
    })();
  }, [groupId]);

  const stats = useMemo(() => {
    const total = rows.length;
    const eligible = rows.filter((r) => r.rate >= 80).length;
    const avg = total > 0 ? Math.round(rows.reduce((s, r) => s + r.rate, 0) / total) : 0;
    return { total, eligible, avg };
  }, [rows]);

  function exportCsv() {
    if (!rows.length) return;
    const group = groups.find((g) => g.id === groupId);
    exportToCsv(
      `Rotation_${(group?.name ?? "group").replace(/[^a-z0-9]+/gi, "_")}_${format(new Date(), "yyyy-MM-dd")}.csv`,
      ["SN", "Student", "Reg #", "Scheduled days", "Present days", "Rate %", "Eligibility"],
      rows.map((r, i) => [
        String(i + 1),
        r.student_name,
        r.student_number,
        String(r.scheduled_days),
        String(r.present_days),
        String(r.rate),
        r.rate >= 80 ? "Eligible" : "Not Eligible",
      ]),
    );
  }

  const selected = groups.find((g) => g.id === groupId);
  const siteLabel = selected ? (selected.field_sites?.name ?? selected.site_name_override ?? "Ad-hoc") : "";

  return (
    <div className="space-y-4">
      <RotationEligibilityAlerts />
      <RotationReportsView />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><FileSpreadsheet className="w-4 h-4" /> Rotation Report</CardTitle>
          <CardDescription>Per-student attendance for a field group.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Field group</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger><SelectValue placeholder="Select a group…" /></SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name} — {g.class} (Sem {g.semester}) — {g.field_sites?.name ?? g.site_name_override ?? "Ad-hoc"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button className="w-full" onClick={exportCsv} disabled={!rows.length}>
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {selected && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{selected.name}</CardTitle>
            <CardDescription>{selected.class} • Semester {selected.semester} • {siteLabel}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-center">
              <Card><CardContent className="py-3"><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-muted-foreground">Students</p></CardContent></Card>
              <Card><CardContent className="py-3"><p className="text-2xl font-bold">{stats.avg}%</p><p className="text-xs text-muted-foreground">Avg attendance</p></CardContent></Card>
              <Card><CardContent className="py-3"><p className="text-2xl font-bold">{stats.eligible}/{stats.total}</p><p className="text-xs text-muted-foreground">Eligible (≥80%)</p></CardContent></Card>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">SN</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Reg #</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Present</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Eligibility</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No data.</TableCell></TableRow>
                ) : rows.map((r, i) => (
                  <TableRow key={r.student_id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{r.student_name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.student_number}</TableCell>
                    <TableCell>{r.scheduled_days}</TableCell>
                    <TableCell>{r.present_days}</TableCell>
                    <TableCell>{r.rate}%</TableCell>
                    <TableCell>
                      <Badge variant={r.rate >= 80 ? "default" : "destructive"}>{r.rate >= 80 ? "Eligible" : "Not Eligible"}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
